import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export default function PaymentResult({ mode }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshCart } = useStore();
  const [state, setState] = useState(mode === "cancel" ? "cancel" : "checking");
  const [orderId, setOrderId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (mode === "cancel") return;
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      try {
        const res = await api.paymentStatus(sessionId);
        if (!active) return;
        if (res.payment_status === "paid") {
          setOrderId(res.order_id);
          setState("success");
          refreshCart();
          return;
        }
        if (["failed", "expired"].includes(res.payment_status)) { setState("error"); return; }
      } catch (e) { if (process.env.NODE_ENV === "development") console.error("payment status poll failed", e); }
      attempts.current += 1;
      if (attempts.current >= 12) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [mode, params, refreshCart]);

  const Wrap = ({ children }) => (
    <main data-testid="payment-result-page" className="min-h-screen bg-cream flex items-center justify-center px-6 pt-28 pb-24">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="max-w-md w-full text-center">{children}</motion.div>
    </main>
  );

  if (state === "checking") return (
    <Wrap>
      <div data-testid="payment-checking" className="flex flex-col items-center gap-5">
        <Loader2 size={40} className="text-mocha-dark animate-spin" />
        <h1 className="font-serif text-3xl text-ink">Confirmăm plata...</h1>
        <p className="text-ink-soft text-sm">Te rugăm să aștepți câteva momente, nu închide pagina.</p>
      </div>
    </Wrap>
  );

  if (state === "success") return (
    <Wrap>
      <div data-testid="payment-success" className="flex flex-col items-center gap-5">
        <div className="w-20 h-20 rounded-full bg-lavender flex items-center justify-center text-lavender-dusty"><Check size={40} /></div>
        <h1 className="font-serif text-4xl text-ink">Plată reușită!</h1>
        <p className="text-ink-soft">Îți mulțumim! Comanda ta a fost plasată și plătită cu succes.</p>
        {orderId ? <p className="text-xs text-ink-muted">Cod comandă: {orderId.slice(0, 8).toUpperCase()}</p> : null}
        <button data-testid="payment-continue-btn" onClick={() => navigate("/")}
          className="mt-4 px-8 py-3.5 bg-ink text-cream text-xs uppercase tracking-[0.2em] hover:bg-mocha-dark transition-colors">Continuă cumpărăturile</button>
      </div>
    </Wrap>
  );

  if (state === "cancel") return (
    <Wrap>
      <div data-testid="payment-cancel" className="flex flex-col items-center gap-5">
        <div className="w-20 h-20 rounded-full bg-beige flex items-center justify-center text-mocha-dark"><X size={38} /></div>
        <h1 className="font-serif text-4xl text-ink">Plată anulată</h1>
        <p className="text-ink-soft">Nicio sumă nu a fost debitată. Produsele tale sunt încă în coș.</p>
        <button data-testid="payment-back-btn" onClick={() => navigate("/")}
          className="mt-4 px-8 py-3.5 bg-ink text-cream text-xs uppercase tracking-[0.2em] hover:bg-mocha-dark transition-colors">Înapoi la magazin</button>
      </div>
    </Wrap>
  );

  return (
    <Wrap>
      <div data-testid="payment-error" className="flex flex-col items-center gap-5">
        <div className="w-20 h-20 rounded-full bg-beige flex items-center justify-center text-destructive"><X size={38} /></div>
        <h1 className="font-serif text-4xl text-ink">{state === "timeout" ? "Verificare în curs" : "Ceva n-a mers"}</h1>
        <p className="text-ink-soft">{state === "timeout"
          ? "Plata durează mai mult decât de obicei. Dacă ai fost debitat, comanda va fi confirmată automat."
          : "Nu am putut confirma plata. Dacă ai fost debitat, contactează-ne."}</p>
        <button onClick={() => navigate("/")} className="mt-4 px-8 py-3.5 bg-ink text-cream text-xs uppercase tracking-[0.2em] hover:bg-mocha-dark transition-colors">Înapoi la magazin</button>
      </div>
    </Wrap>
  );
}
